import React from "react";
import { withStyles } from "material-ui/styles";
import { connect } from "react-redux";
import { compose } from "recompose";
import Typography from "material-ui/Typography";
import Paper from "material-ui/Paper";
import Grid from "material-ui/Grid";
import Icon from "material-ui/Icon";
import Table, {
  TableBody,
  TableCell,
  TableRow
} from "material-ui/Table";
import Title from "../ui/title";
import Content from "../ui/content";
import Back from "../ui/back";
import TestsRowLink from "../table/cells/testsRowLink";
import { dateTimeFormatter } from "../../common/utils";

const styles = theme => ({
  paper: theme.mixins.gutters({
    paddingTop: 16,
    paddingBottom: 16,
    height: 110
  }),
  infotile: {
    backgroundColor: "#1c408c"
  },
  text: {
    color: "#FFFFFF",
    fontWeight: "300",
    letterSpacing: "0.5px"
  },
  heading: {
    fontSize: 20,
    marginTop: 5,
    fontWeight: 500
  },
  tileIcon: {
    fontSize: 56,
    color: "#FFFFFF"
  },
  section: {
    padding: 24,
    overflowX: "auto"
  },
  sectionTitle: {
    marginBottom: 16
  },
  labelCell: {
    fontWeight: 600,
    width: 240
  }
});

const formatDateTime = value => (value ? dateTimeFormatter(value) : "-");
const formatValue = value => (value === null || typeof value === "undefined" || value === "" ? "-" : value);
const formatTipWearCounter = value => (value === null || typeof value === "undefined" || value === "" || value === 0 || value === "0" ? "-" : value);

const supportsAdapterMetadata = adapter => {
  const typeText = `${adapter.adaptertypename || ""} ${adapter.moduletype || ""}`.toLowerCase();
  return /tier[\s-]*1|fiber\s*tek/.test(typeText);
};

class Adapter extends React.Component {
  componentDidMount() {
    const { adapterSerialNo, adapterAssetInfo } = this.props;
    adapterAssetInfo(adapterSerialNo);
  }

  componentDidUpdate(prevProps) {
    const { adapterSerialNo, adapterAssetInfo } = this.props;
    if (prevProps.adapterSerialNo !== adapterSerialNo) {
      adapterAssetInfo(adapterSerialNo);
    }
  }

  renderInfoTile(label, value, iconName) {
    const { classes } = this.props;

    return (
      <Paper className={`${classes.paper} ${classes.infotile}`}>
        <Grid container spacing={0}>
          <Grid item xs={10}>
            <Typography type="button" className={classes.text}>
              {label}
            </Typography>
            <Typography type="heading" className={`${classes.text} ${classes.heading}`}>
              {value}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Icon className={classes.tileIcon}>{iconName}</Icon>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  render() {
    const { adapterAsset, adapterSerialNo, classes, translate, impersonating, softwareUpdate } = this.props;
    const contentTranslate = typeof translate === "function" ? translate : value => value;
    let margintop = "150px";

    if (impersonating && softwareUpdate) {
      margintop = "275px";
    } else if (impersonating || softwareUpdate) {
      margintop = "215px";
    }
    const pageShellStyle = {
      paddingTop: margintop,
      boxSizing: "border-box",
      height: "100vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column"
    };

    if (!adapterAsset || adapterAsset.adapterserialno !== adapterSerialNo) {
      return (
        <div style={pageShellStyle}>
          <Title
            back={<Back link="/devices" title={contentTranslate("general_devices")} />}
            backCheck={<Back link="/devices?view=adapters" title={contentTranslate("adapter_assets_title")} />}
            title={contentTranslate("adapter_management_title")}
          />
          <Content translate={contentTranslate} fillHeight scrollable>
            <Paper className={classes.section}>
              <Typography type="body1">{contentTranslate("adapter_details_loading")}</Typography>
            </Paper>
          </Content>
        </div>
      );
    }

    const linkedDeviceLink = adapterAsset.linkeddeviceid
      ? `/device/${adapterAsset.linkeddeviceid}?from=adapters`
      : null;

    return (
      <div style={pageShellStyle}>
        <Title
          back={<Back link="/devices" title={contentTranslate("general_devices")} />}
          backCheck={<Back link="/devices?view=adapters" title={contentTranslate("adapter_assets_title")} />}
          title={contentTranslate("adapter_management_title")}
        />
        <Content translate={contentTranslate} fillHeight scrollable>
          {adapterAsset ? (
            <React.Fragment>
              <Grid container spacing={24}>
                <Grid item xs={4}>
                  {this.renderInfoTile(contentTranslate("adapter_serial"), formatValue(adapterAsset.adapterserialno), "memory")}
                </Grid>
                <Grid item xs={4}>
                  {this.renderInfoTile(contentTranslate("adapter_type"), formatValue(adapterAsset.adaptertypename), "settings_input_component")}
                </Grid>
                <Grid item xs={4}>
                  {this.renderInfoTile(contentTranslate("adapter_module_type"), formatValue(adapterAsset.moduletype), "developer_board")}
                </Grid>
                {supportsAdapterMetadata(adapterAsset) && (
                  <Grid item xs={4}>
                    {this.renderInfoTile(contentTranslate("adapter_firmware"), formatValue(adapterAsset.firmwareversion), "sync")}
                  </Grid>
                )}
                {supportsAdapterMetadata(adapterAsset) && (
                  <Grid item xs={4}>
                    {this.renderInfoTile(contentTranslate("adapter_calibration_due"), formatDateTime(adapterAsset.calibrationduedate), "build")}
                  </Grid>
                )}
              </Grid>

              <Paper className={classes.section} style={{ marginTop: 24 }}>
                <Typography type="title" className={classes.sectionTitle}>
                  {contentTranslate("adapter_details_title")}
                </Typography>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("adapter_calibration_date")}</TableCell>
                      <TableCell>{formatDateTime(adapterAsset.calibrationdate)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("adapter_last_result")}</TableCell>
                      <TableCell>{formatDateTime(adapterAsset.lastresultdatetime)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("metrics_technician")}</TableCell>
                      <TableCell>{formatValue(adapterAsset.technician)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("adapter_tip_wear_counter")}</TableCell>
                      <TableCell>{formatTipWearCounter(adapterAsset.tipwearcounter)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("metrics_test_count")}</TableCell>
                      <TableCell>{formatValue(adapterAsset.testcount)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("adapter_location")}</TableCell>
                      <TableCell>
                        {adapterAsset.lastlatitude !== null && typeof adapterAsset.lastlatitude !== "undefined" &&
                        adapterAsset.lastlongitude !== null && typeof adapterAsset.lastlongitude !== "undefined"
                          ? `${adapterAsset.lastlatitude}, ${adapterAsset.lastlongitude}`
                          : "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className={classes.labelCell}>{contentTranslate("adapter_linked_device")}</TableCell>
                      <TableCell>
                        {linkedDeviceLink && adapterAsset.linkeddeviceserialno ? (
                          <TestsRowLink
                            title={adapterAsset.linkeddeviceserialno}
                            link={linkedDeviceLink}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>
            </React.Fragment>
          ) : (
            <Paper className={classes.section}>
              <Typography type="body1">{contentTranslate("adapter_details_unavailable")}</Typography>
            </Paper>
          )}
        </Content>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  impersonating: state.userReducer.impersonating,
  softwareUpdate: state.userReducer.softwareUpdate
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps)
)(Adapter);
